/**
 * DeviceManager — MDM-lite device management panel for admin users.
 *
 * Lists all enrolled devices for the org with status badges, platform icons,
 * last-seen timestamps, and actions: wipe, suspend, reactivate, assign policy.
 */
import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { authHeaders, errorMessage, showAdminError } from './adminApi';
import { tv, relTime } from './i18nUtil';

interface Device {
  id: string; device_name: string; platform: string; os_version?: string;
  app_version?: string; status: string; last_seen_at: string | null;
  enrolled_at: string; user_id: string; is_managed: boolean; policy_name?: string;
}
interface DevicePolicy { id: string; name: string; }

const PLATFORM_ICONS: Record<string, string> = {
  WEB:          '🌐',
  DESKTOP_WIN:  '🪟',
  DESKTOP_MAC:  '🍎',
  DESKTOP_LINUX:'🐧',
  MOBILE_IOS:   '📱',
  MOBILE_ANDROID:'🤖',
};

const STATUS_COLOR: Record<string, string> = {
  ENROLLED:   'bg-blue-100   text-blue-800',
  ACTIVE:     'bg-green-100  text-green-800',
  SUSPENDED:  'bg-yellow-100 text-yellow-800',
  WIPED:      'bg-red-100    text-red-800',
  LOST:       'bg-orange-100 text-orange-800',
};

async function apiFetch<T>(url: string, opts?: RequestInit): Promise<T> {
  // session token + Content-Type only with a body (see adminApi.ts); failed actions are shown
  const headers: Record<string, string> = { ...authHeaders(), ...(opts?.body != null ? { 'Content-Type': 'application/json' } : {}), ...(opts?.headers as Record<string, string> | undefined) };
  const res = await fetch(`/api${url}`, { ...opts, headers });
  if (!res.ok) {
    const msg = await errorMessage(res);
    if ((opts?.method ?? 'GET') !== 'GET') showAdminError(msg);
    throw new Error(msg);
  }
  return (await res.json() as { data: T }).data;
}


export function DeviceManager() {
  const { t }  = useTranslation();
  const qc     = useQueryClient();
  const [confirmWipe, setConfirmWipe] = useState<string | null>(null);
  const [policyModal, setPolicyModal] = useState<string | null>(null);
  const [selectedPolicy, setSelectedPolicy] = useState('');

  const { data: devices = [], isLoading } = useQuery<Device[]>({
    queryKey: ['admin', 'devices'],
    queryFn:  () => apiFetch('/v1/devices').then((d: unknown) => (d as { devices: Device[] }).devices),
    refetchInterval: 30_000,
  });

  const { data: policies = [] } = useQuery<DevicePolicy[]>({
    queryKey: ['admin', 'device-policies'],
    queryFn:  () => apiFetch('/v1/device-policies').then((d: unknown) => (d as { policies: DevicePolicy[] }).policies),
  });

  const wipe = useMutation({
    mutationFn: (id: string) => apiFetch(`/v1/devices/${id}/wipe`, { method: 'POST' }),
    onSuccess:  () => { void qc.invalidateQueries({ queryKey: ['admin', 'devices'] }); setConfirmWipe(null); },
  });

  const suspend = useMutation({
    mutationFn: (id: string) => apiFetch(`/v1/devices/${id}/suspend`, { method: 'POST' }),
    onSuccess:  () => void qc.invalidateQueries({ queryKey: ['admin', 'devices'] }),
  });

  const reactivate = useMutation({
    mutationFn: (id: string) => apiFetch(`/v1/devices/${id}/reactivate`, { method: 'POST' }),
    onSuccess:  () => void qc.invalidateQueries({ queryKey: ['admin', 'devices'] }),
  });

  const assignPolicy = useMutation({
    mutationFn: ({ deviceId, policyId }: { deviceId: string; policyId: string }) =>
      apiFetch(`/v1/devices/${deviceId}/policy`, { method: 'POST', body: JSON.stringify({ policyId }) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'devices'] });
      setPolicyModal(null);
    },
  });

  const handleAssignPolicy = useCallback(() => {
    if (policyModal && selectedPolicy) assignPolicy.mutate({ deviceId: policyModal, policyId: selectedPolicy });
  }, [policyModal, selectedPolicy, assignPolicy]);

  if (isLoading) {
    return <div className="animate-pulse p-6 text-slate-400">{t('common.loading', 'Loading…')}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900">{t('adminUi.devices.title', 'Device Management')}</h2>
        <span className="text-sm text-slate-500">{tv(t, 'adminUi.devices.enrolled', '{{n}} enrolled', { n: devices.length })}</span>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              {[
                t('adminUi.devices.colDevice', 'Device'), t('adminUi.devices.colPlatform', 'Platform'), t('adminUi.devices.colOs', 'OS'),
                t('adminUi.colStatus', 'Status'), t('adminUi.devices.colPolicy', 'Policy'), t('adminUi.devices.colLastSeen', 'Last Seen'),
                t('adminUi.colActions', 'Actions'),
              ].map((h, i) => (
                <th key={i} className="px-4 py-3 text-left whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {devices.map(d => (
              <tr key={d.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 font-medium text-slate-900">{d.device_name}</td>
                <td className="px-4 py-3 text-center">{PLATFORM_ICONS[d.platform] ?? '?'}</td>
                <td className="px-4 py-3 text-slate-500">{d.os_version ?? '—'}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLOR[d.status] ?? 'bg-slate-100 text-slate-700'}`}>
                    {d.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-500">{d.policy_name ?? <span className="text-slate-300">—</span>}</td>
                <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{relTime(t, d.last_seen_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {d.status === 'ACTIVE' && (
                      <button
                        onClick={() => suspend.mutate(d.id)}
                        className="rounded px-2 py-1 text-xs bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-200"
                      >
                        {t('adminUi.devices.suspend', 'Suspend')}
                      </button>
                    )}
                    {d.status === 'SUSPENDED' && (
                      <button
                        onClick={() => reactivate.mutate(d.id)}
                        className="rounded px-2 py-1 text-xs bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
                      >
                        {t('adminUi.devices.reactivate', 'Reactivate')}
                      </button>
                    )}
                    <button
                      onClick={() => setPolicyModal(d.id)}
                      className="rounded px-2 py-1 text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"
                    >
                      {t('adminUi.devices.policy', 'Policy')}
                    </button>
                    {d.status !== 'WIPED' && (
                      <button
                        onClick={() => setConfirmWipe(d.id)}
                        className="rounded px-2 py-1 text-xs bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
                      >
                        {t('adminUi.devices.wipe', 'Wipe')}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Wipe confirmation modal */}
      {confirmWipe && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl space-y-4">
            <h3 className="font-semibold text-lg text-red-600">{t('adminUi.devices.wipeConfirm', 'Confirm Remote Wipe')}</h3>
            <p className="text-sm text-slate-600">
              {t('adminUi.devices.wipeWarning', 'This will request a full data wipe on the device. This action cannot be undone.')}
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmWipe(null)}
                className="rounded-lg px-4 py-2 text-sm border border-slate-200 hover:bg-slate-50">
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={() => wipe.mutate(confirmWipe)}
                disabled={wipe.isPending}
                className="rounded-lg px-4 py-2 text-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {wipe.isPending ? t('adminUi.devices.wiping', 'Wiping…') : t('adminUi.devices.wipeConfirmBtn', 'Wipe Device')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Policy assignment modal */}
      {policyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl space-y-4">
            <h3 className="font-semibold text-lg text-slate-900">{t('adminUi.devices.assignPolicy', 'Assign Policy')}</h3>
            <select
              value={selectedPolicy}
              onChange={e => setSelectedPolicy(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700"
            >
              <option value="">{t('adminUi.devices.selectPolicy', 'Select a policy…')}</option>
              {policies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setPolicyModal(null)}
                className="rounded-lg px-4 py-2 text-sm border border-slate-200 hover:bg-slate-50">
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleAssignPolicy}
                disabled={!selectedPolicy || assignPolicy.isPending}
                className="rounded-lg px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {assignPolicy.isPending ? t('adminUi.saving', 'Saving…') : t('common.save', 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
